import type {ValueType} from '@github-ui/custom-properties-types'
import type {FilterProviderOptions} from '@github-ui/filter-core'
import {defaultFilterProviderOptions, TRUE_FALSE_FILTER_VALUES} from '@github-ui/filter-core'
import {NestedFilterProvider} from '@github-ui/filter-core/providers'
import {NoteIcon, PlugIcon} from '@primer/octicons-react'

export interface PropertyDefinition {
  propertyName: string
  allowedValues?: string[] | null
  required?: boolean
  valueType: ValueType
  type?: string
}

const prefixProviderOptions = {
  filterTypes: {valueless: false, hasValue: false},
}

export function getCustomPropertiesProvider(definitions: PropertyDefinition[]) {
  return new NestedFilterProvider(
    {
      key: 'props',
      displayName: 'Custom properties',
      description: 'Filter by custom properties',
      priority: 3,
      icon: NoteIcon,
      subKeys: definitions.map(d => buildSubKey(d)),
    },
    prefixProviderOptions,
  )
}

type SubKeyType = 'select' | 'text'

function buildSubKey(definition: PropertyDefinition) {
  const {propertyName, valueType} = definition
  const values =
    valueType === 'true_false'
      ? TRUE_FALSE_FILTER_VALUES
      : (definition.allowedValues || []).map((value, index) => ({value, displayName: value, priority: index + 1}))

  const subKeyType: SubKeyType = ['string', 'url'].includes(valueType) ? 'text' : 'select'

  return {
    key: propertyName,
    displayName: `Property: ${propertyName}`,
    icon: definition.type === 'external' ? PlugIcon : NoteIcon,
    type: subKeyType,
    values,
    options: getFilterOptions(definition),
  }
}

function getFilterOptions({valueType, required}: PropertyDefinition): FilterProviderOptions {
  return {
    ...defaultFilterProviderOptions,
    filterTypes: {
      multiKey: valueType === 'multi_select' ? true : false,
      multiValue: true,
      valueless: !required,
      hasValue: !required,
    },
  }
}

export function isCustomPropertiesKey(key: string) {
  const lowerKey = key.toLowerCase()
  return lowerKey.startsWith('properties.') || lowerKey.startsWith('props.') || lowerKey.startsWith('p.')
}
